import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import ensureLeafletIcons from './leafletConfig';
import { isValidCoordinate } from './utils';

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export default function UidMap({
  points,
  height = 220,
  showTrail = true,
  autoFit = true,
  children,
  style = {},
  maxZoom = 18,
  className,
}) {
  ensureLeafletIcons();

  const coordinates = useMemo(() => {
    return (Array.isArray(points) ? points : []).filter(point => isValidCoordinate(point.lat) && isValidCoordinate(point.lon));
  }, [points]);

  const center = useMemo(() => {
    if (!coordinates.length) {
      return [0, 0];
    }
    if (coordinates.length === 1 || !autoFit) {
      return [coordinates[coordinates.length - 1].lat, coordinates[coordinates.length - 1].lon];
    }
    const avgLat = coordinates.reduce((acc, point) => acc + point.lat, 0) / coordinates.length;
    const avgLon = coordinates.reduce((acc, point) => acc + point.lon, 0) / coordinates.length;
    return [avgLat, avgLon];
  }, [coordinates, autoFit]);

  if (!coordinates.length) {
    return (
      <div
        className={className}
        style={{
          ...placeholderStyle,
          ...style,
          height,
        }}
      >
        <span>No coordinates</span>
      </div>
    );
  }

  return (
    <MapContainer
      className={className}
      center={center}
      zoom={autoFit ? undefined : 10}
      style={{ height, width: '100%', borderRadius: '12px', ...style }}
      scrollWheelZoom={false}
      dragging={coordinates.length > 1}
      doubleClickZoom={false}
      attributionControl={false}
      preferCanvas
    >
      <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
      {showTrail && coordinates.length > 1 && (
        <Polyline positions={coordinates.map(point => [point.lat, point.lon])} color="#46BB89" weight={4} opacity={0.7} />
      )}
      {autoFit && <FitBounds coordinates={coordinates} maxZoom={maxZoom} />}
      {coordinates.map((point, index) => (
        <CircleMarker
          key={`${point.lat}-${point.lon}-${index}`}
          center={[point.lat, point.lon]}
          radius={index === coordinates.length - 1 ? 7 : 5}
          color={index === coordinates.length - 1 ? '#1d9273' : '#888'}
          weight={2}
          opacity={0.9}
          fillOpacity={index === coordinates.length - 1 ? 0.9 : 0.5}
        />
      ))}
      {children}
    </MapContainer>
  );
}

const placeholderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(70,187,137,0.12), rgba(70,187,137,0.04))',
  color: '#6b7280',
  fontSize: '0.85rem',
};

function FitBounds({ coordinates, maxZoom }) {
  const map = useMap();

  useEffect(() => {
    if (!coordinates.length) {
      return;
    }
    if (coordinates.length === 1) {
      map.setView([coordinates[0].lat, coordinates[0].lon], Math.min(maxZoom, 12));
      return;
    }
    const bounds = L.latLngBounds(coordinates.map(point => [point.lat, point.lon]));
    map.fitBounds(bounds, { maxZoom: Math.min(maxZoom, 12) });
  }, [coordinates, map, maxZoom]);

  return null;
}
